import axios from 'axios';
import { apiClient } from './client';
import type {
  PlatformStats,
  PlatformSettings,
  GrowthPoint,
  ServerHealth,
  Announcement,
  CreateAnnouncementInput,
  User,
  Client,
} from '../types';

export async function getPlatformStats(): Promise<PlatformStats> {
  const { data } = await apiClient.get<PlatformStats>('/api/platform/stats');
  return data;
}

export async function getPlatformGrowth(months = 12): Promise<GrowthPoint[]> {
  const { data } = await apiClient.get<GrowthPoint[]>('/api/platform/growth', { params: { months } });
  return data;
}

export async function getPlatformHealth(): Promise<ServerHealth[]> {
  const { data } = await apiClient.get<ServerHealth[]>('/api/platform/health');
  return data;
}

// Public — no auth token. Used by the login page.
export interface PublicInfo {
  platform_name: string;
  logo_url: string;
  favicon_url: string;
}

export async function getPublicInfo(): Promise<PublicInfo> {
  const { data } = await axios.get<PublicInfo>('/api/info');
  return data;
}

export async function getPlatformSettings(): Promise<PlatformSettings> {
  const { data } = await apiClient.get<PlatformSettings>('/api/platform/settings');
  return data;
}

export async function updatePlatformSettings(
  payload: Partial<PlatformSettings>
): Promise<PlatformSettings> {
  const { data } = await apiClient.put<PlatformSettings>('/api/platform/settings', payload);
  return data;
}

export async function suspendClient(id: number, reason: string): Promise<void> {
  await apiClient.post(`/api/clients/${id}/suspend`, { reason });
}

export async function unsuspendClient(id: number): Promise<void> {
  await apiClient.post(`/api/clients/${id}/unsuspend`);
}

export interface ImpersonateResponse {
  token: string;
  user?: User;
  client: Client;
}

export async function impersonateClient(id: number): Promise<ImpersonateResponse> {
  const { data } = await apiClient.post<ImpersonateResponse>(`/api/clients/${id}/impersonate`);
  return data;
}

export async function getAnnouncements(): Promise<Announcement[]> {
  const { data } = await apiClient.get<Announcement[]>('/api/announcements');
  return data;
}

export async function createAnnouncement(
  payload: CreateAnnouncementInput
): Promise<Announcement> {
  const { data } = await apiClient.post<Announcement>('/api/announcements', payload);
  return data;
}

export async function updateAnnouncement(
  id: number,
  payload: Partial<Announcement>
): Promise<Announcement> {
  const { data } = await apiClient.put<Announcement>(`/api/announcements/${id}`, payload);
  return data;
}

export async function deleteAnnouncement(id: number): Promise<void> {
  await apiClient.delete(`/api/announcements/${id}`);
}
